import { CancelTokenSource } from 'axios';

import BaseAPI from 'utils/api/base';

import { APIRequest, PaginatedResponse, UuidableName } from 'utils/api/types';

import {
  CreateMemberGroupRequest,
  MemberGroupDetailResponse,
  MemberGroupHistoryRow,
  MemberGroupListItem,
  MemberGroupSearchResult,
  MemberGroupUsageResponse,
  MemberRow,
  PreviewCounts,
  PreviewMembersParams,
  PreviewRequest,
  UpdateMemberGroupRequest,
  UserMemberGroupRow,
} from './memberGroups.types';

class MemberGroupsAPI extends BaseAPI {
  listMemberGroups = (
    params?: Partial<{
      active: string;
      type: string;
      members: string;
      query: string;
      ordering: string;
      limit: number;
      offset: number;
    }>
  ): APIRequest<PaginatedResponse<MemberGroupListItem>> => {
    const url = '/member-groups/';
    const method = 'GET';

    return this.request<PaginatedResponse<MemberGroupListItem>>({ url, method, params });
  };

  searchMemberGroups = (
    params?: Partial<{ query: string; limit: number; offset: number }>,
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<MemberGroupSearchResult[]> => {
    const url = '/member-groups/search/';
    const method = 'GET';

    return this.request<MemberGroupSearchResult[]>({ url, method, params, cancelTokenSource });
  };

  createMemberGroup = (data: CreateMemberGroupRequest): APIRequest<MemberGroupDetailResponse> => {
    const url = '/member-groups/';
    const method = 'POST';

    return this.request<MemberGroupDetailResponse>({ url, method, data });
  };

  loadMemberGroup = (uuid: string): APIRequest<MemberGroupDetailResponse> => {
    const url = `/member-groups/${uuid}/`;
    const method = 'GET';

    return this.request<MemberGroupDetailResponse>({ url, method });
  };

  updateMemberGroup = (uuid: string, data: UpdateMemberGroupRequest): APIRequest<MemberGroupDetailResponse> => {
    const url = `/member-groups/${uuid}/`;
    const method = 'PATCH';

    return this.request<MemberGroupDetailResponse>({ url, method, data });
  };

  loadMemberGroupUsage = (
    uuid: string,
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<MemberGroupUsageResponse> => {
    const url = `/member-groups/${uuid}/usage/`;
    const method = 'GET';

    return this.request<MemberGroupUsageResponse>({ url, method, cancelTokenSource });
  };

  addMembers = (uuid: string, memberUuids: string[]): APIRequest<MemberGroupDetailResponse> => {
    const url = `/member-groups/${uuid}/members/`;
    const method = 'POST';

    return this.request<MemberGroupDetailResponse>({ url, method, data: { memberUuids } });
  };

  removeMember = (uuid: string, userUuid: string): APIRequest<void> => {
    const url = `/member-groups/${uuid}/members/${userUuid}/`;
    const method = 'DELETE';

    return this.request<void>({ url, method });
  };

  listMembers = (
    uuid: string,
    params?: Partial<{ query: string; ordering: string; limit: number; offset: number }>
  ): APIRequest<PaginatedResponse<MemberRow>> => {
    const url = `/member-groups/${uuid}/members/`;
    const method = 'GET';

    return this.request<PaginatedResponse<MemberRow>>({ url, method, params });
  };

  listHistory = (
    uuid: string,
    params?: Partial<{ limit: number; offset: number }>
  ): APIRequest<PaginatedResponse<MemberGroupHistoryRow>> => {
    const url = `/member-groups/${uuid}/history/`;
    const method = 'GET';

    return this.request<PaginatedResponse<MemberGroupHistoryRow>>({ url, method, params });
  };

  listHistoryMembers = (
    uuid: string,
    entryUuid: string,
    params?: Partial<{ limit: number; offset: number }>,
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<PaginatedResponse<UuidableName | null>> => {
    const url = `/member-groups/${uuid}/history/${entryUuid}/members/`;
    const method = 'GET';

    return this.request<PaginatedResponse<UuidableName | null>>({ url, method, params, cancelTokenSource });
  };

  listUserMemberGroups = (
    userUuid: string,
    params?: Partial<{ query: string; limit: number; offset: number }>,
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<PaginatedResponse<UserMemberGroupRow>> => {
    const url = `/member-groups/users/${userUuid}/`;
    const method = 'GET';

    return this.request<PaginatedResponse<UserMemberGroupRow>>({ url, method, params, cancelTokenSource });
  };

  previewCounts = (data: PreviewRequest): APIRequest<PreviewCounts> => {
    const url = '/member-groups/preview/counts/';
    const method = 'POST';

    return this.request<PreviewCounts>({ url, method, data });
  };

  previewMembers = (data: PreviewRequest, params: PreviewMembersParams): APIRequest<PaginatedResponse<MemberRow>> => {
    const url = '/member-groups/preview/members/';
    const method = 'POST';

    return this.request<PaginatedResponse<MemberRow>>({ url, method, data, params });
  };
}

export default MemberGroupsAPI;
